import React from 'react';
import { Eye, Users } from 'lucide-react';
import Tooltip from '../ui/Tooltip';

// Shared by every post card (Posts page, Brand Value, Content Performance) so collab posts and
// unreported views read the same everywhere.

// Post types Instagram reports no view count for unless enriched via the Enhanced (Graph) API.
// Scrape paths store 'Image'/'Sidecar' (raw Apify) or 'post'/'carousel' (parser), so compare lowercased.
const STILL_POST_TYPES = new Set(['image', 'post', 'carousel', 'carousel_album', 'sidecar']);

/**
 * Owner of a collab post that belongs to another account. Social ingest credits such posts to the
 * scraped account and records the real owner as the collaborator; some endpoints return a
 * comma-joined list, owner first. Null for the account's own posts.
 */
export const getCollabOwner = (collaboratorUsername?: string | null): string | null =>
  collaboratorUsername?.split(',')[0].trim() || null;

/**
 * A 0 here usually means "not reported", not "nobody saw it": collabs can't be enriched (Graph only
 * returns media the connected account owns), and photos/carousels only get views via the Enhanced API.
 */
export const isViewsUnavailable = (opts: {
  isInstagram: boolean;
  views: number | null | undefined;
  postType: string | null | undefined;
  collabOwner: string | null;
}): boolean =>
  opts.isInstagram &&
  !opts.views &&
  (!!opts.collabOwner || STILL_POST_TYPES.has((opts.postType || '').toLowerCase()));

export const CollabBadge: React.FC<{ owner: string; creditedTo: string }> = ({ owner, creditedTo }) => (
  <Tooltip
    content={`Collab post owned by @${owner}. It appears here because @${creditedTo} is a collaborator, and its EMV is credited to @${creditedTo}.`}
    placement="top"
    maxWidth={240}
    wrapperClassName="inline-flex flex-shrink-0"
  >
    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] leading-none font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-600 border border-indigo-100">
      <Users className="h-2.5 w-2.5" />
      Collab
    </span>
  </Tooltip>
);

export const UnavailableViews: React.FC<{ collabOwner: string | null }> = ({ collabOwner }) => (
  <Tooltip
    content={
      collabOwner
        ? `Views aren't available: this post is owned by @${collabOwner}, and Instagram only reports insights to the owning account. EMV counts likes and comments only.`
        : "Views aren't available: Instagram reports views for photos and carousels only through the Enhanced API connection. EMV counts likes and comments only."
    }
    placement="top"
    maxWidth={240}
  >
    <div className="flex items-center gap-0.5 text-gray-300 cursor-help">
      <Eye className="h-3 w-3" />
      <span>—</span>
    </div>
  </Tooltip>
);
